package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.Box
import dev.lingvo.shared.screenshots.doc.DecodeResult
import dev.lingvo.shared.screenshots.doc.DesignCodec
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.LayerBase
import dev.lingvo.shared.screenshots.doc.LayerPatch
import dev.lingvo.shared.screenshots.doc.LocaleOverride
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Paint
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.doc.StoreDeviceClassRule
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.doc.TextStyle
import dev.lingvo.shared.screenshots.geom.aabb
import dev.lingvo.shared.screenshots.render.upgradeLocaleOverrides
import dev.lingvo.shared.screenshots.scene.SceneArranger
import dev.lingvo.shared.screenshots.scene.SceneInput

/** Board and page identities prevent a delayed panel event from editing a different shot. */
internal data class EditTarget(val boardId: String, val pageId: String)

/** Immutable commands can be previewed and cancelled without touching history or autosave. */
internal fun interface EditCommand {
    fun apply(design: ScreenshotDesign): ScreenshotDesign
}

/** Typed property changes keep caption content outside the design-edit channel. */
internal sealed interface LayerProperty {
    data class Name(val value: String) : LayerProperty
    data class Hidden(val value: Boolean) : LayerProperty
    data class Locked(val value: Boolean) : LayerProperty
    data class Opacity(val value: Double) : LayerProperty
    data class Fill(val value: Paint) : LayerProperty
    data class Text(val value: TextStyle) : LayerProperty
    data class Fit(val value: dev.lingvo.shared.screenshots.doc.FitSpec) : LayerProperty
    data class Radius(val value: Double) : LayerProperty
    data class Shadow(val value: dev.lingvo.shared.screenshots.doc.Shadow?) : LayerProperty
    data class Finish(val value: dev.lingvo.shared.screenshots.doc.FrameFinish) : LayerProperty
}

/** Every command validates the resulting document before callers can publish a commit. */
internal class EditCommands(private val target: EditTarget, private val nextId: () -> String) {
    fun move(ids: Set<String>, dx: Double, dy: Double): EditCommand = edit { page ->
        val selected = allLayers(page.layers).filter { layer -> layer.base.id in ids && layer !is BackgroundLayer &&
            pathTo(page.layers, layer.base.id).none { it.base.locked } }.associate { it.base.id to it.base.box }
        val moved = moveBoxesInDocument(page, selected, dev.lingvo.shared.screenshots.doc.Point(dx, dy))
        page.copy(layers = mapLayers(page.layers) { layer -> moved[layer.base.id]?.let { layer.withBase(layer.base.copy(box = it)) } ?: layer })
    }

    fun resize(id: String, box: Box): EditCommand = boxes(setOf(id)) { box }

    fun rotate(ids: Set<String>, degrees: Double): EditCommand = boxes(ids) {
        it.copy(rotation = (it.rotation + degrees) % 360)
    }

    fun property(ids: Set<String>, property: LayerProperty): EditCommand = edit { page ->
        page.copy(layers = mapLayers(page.layers) { layer ->
            val ancestorsLocked = pathTo(page.layers, layer.base.id).dropLast(1).any { it.base.locked }
            val lockedProperty = layer.base.locked && layer !is BackgroundLayer && property !is LayerProperty.Locked && property !is LayerProperty.Hidden
            if (layer.base.id !in ids || ancestorsLocked || lockedProperty || layer is BackgroundLayer && property !is LayerProperty.Fill) layer
            else when (property) {
                is LayerProperty.Name -> layer.withBase(layer.base.copy(name = property.value))
                is LayerProperty.Hidden -> layer.withBase(layer.base.copy(hidden = property.value))
                is LayerProperty.Locked -> layer.withBase(layer.base.copy(locked = property.value))
                is LayerProperty.Opacity -> layer.withBase(layer.base.copy(opacity = property.value))
                is LayerProperty.Fill -> when (layer) {
                    is ShapeLayer -> layer.copy(fill = property.value)
                    is BackgroundLayer -> layer.copy(fill = property.value)
                    is TextLayer -> layer.copy(style = layer.style.copy(color = property.value))
                    else -> layer
                }
                is LayerProperty.Text -> if (layer is TextLayer) layer.copy(style = property.value) else layer
                is LayerProperty.Fit -> if (layer is TextLayer) layer.copy(fit = property.value) else layer
                is LayerProperty.Radius -> when (layer) {
                    is ShapeLayer -> layer.copy(cornerRadius = property.value)
                    is ImageLayer -> layer.copy(cornerRadius = property.value)
                    is GroupLayer -> layer.copy(cornerRadius = property.value)
                    else -> layer
                }
                is LayerProperty.Shadow -> layer.withBase(layer.base.copy(shadow = property.value))
                is LayerProperty.Finish -> if (layer is DeviceLayer) layer.copy(finish = property.value) else layer
            }
        })
    }

    fun reorder(id: String, direction: Int, extreme: Boolean = false): EditCommand = edit { page ->
        page.copy(layers = siblings(page.layers, setOf(id)) { layers ->
            val from = layers.indexOfFirst { it.base.id == id }
            require(from >= 0 && layers[from] !is BackgroundLayer) { "The background is pinned" } // i18n-ignore: internal contract diagnostic
            if (layers[from].base.locked) return@siblings layers
            val floor = if (layers.firstOrNull() is BackgroundLayer) 1 else 0
            val to = (if (extreme) { if (direction > 0) layers.lastIndex else floor } else from + direction)
                .coerceIn(floor, layers.lastIndex)
            layers.toMutableList().apply { add(to, removeAt(from)) }
        })
    }

    fun delete(ids: Set<String>): EditCommand = edit { page ->
        fun hasLock(layer: Layer): Boolean = layer.base.locked || layer is GroupLayer && layer.children.any(::hasLock)
        fun remove(layers: List<Layer>): List<Layer> = layers.filter {
            it.base.id !in ids || it is BackgroundLayer || hasLock(it)
        }.map { if (it is GroupLayer && !it.base.locked) it.copy(children = remove(it.children)) else it }
            .filterNot { it is GroupLayer && it.children.isEmpty() }
        cleanPatches(page.copy(layers = remove(page.layers)))
    }

    fun group(ids: Set<String>): EditCommand = edit { page ->
        val groupId = nextId()
        var offsetX = 0.0
        var offsetY = 0.0
        val grouped = siblings(page.layers, ids) { layers ->
            val indices = layers.indices.filter { layers[it].base.id in ids }
            require(indices.size >= 2 && indices.last() - indices.first() + 1 == indices.size) {
                "Group contiguous siblings to preserve stacking order" // i18n-ignore: internal validation diagnostic
            }
            val children = indices.map { layers[it] }
            require(children.none { it is BackgroundLayer || it.base.locked }) { "Pinned or locked layers cannot be grouped" } // i18n-ignore: internal contract diagnostic
            val bounds = children.map { aabb(it.base.box) }
            offsetX = bounds.minOf { it.x }; offsetY = bounds.minOf { it.y }
            val box = Box(offsetX, offsetY, bounds.maxOf { it.right } - offsetX, bounds.maxOf { it.bottom } - offsetY)
            val group = GroupLayer(LayerBase(groupId, "Group", box), children.map {
                it.withBase(it.base.copy(box = it.base.box.copy(x = it.base.box.x - offsetX, y = it.base.box.y - offsetY)))
            })
            layers.take(indices.first()) + group + layers.drop(indices.last() + 1)
        }
        page.copy(layers = grouped, overrides = page.overrides.mapValues { (_, value) -> value.copy(
            patches = value.patches.mapValues { (id, patch) ->
                if (id in ids) patch.copy(box = patch.box?.let { it.copy(x = it.x - offsetX, y = it.y - offsetY) }) else patch
            }) })
    }

    fun ungroup(id: String): EditCommand = edit { page ->
        require(page.overrides.values.none { id in it.patches }) { "Remove group localization before ungrouping" } // i18n-ignore: internal contract diagnostic
        val group = allLayers(page.layers).find { it.base.id == id } as? GroupLayer
            ?: throw IllegalArgumentException("Select a group") // i18n-ignore: internal contract diagnostic
        val childIds = group.children.map { it.base.id }.toSet()
        cleanPatches(page.copy(overrides = page.overrides.mapValues { (_, value) -> value.copy(
            patches = value.patches.mapValues { (childId, patch) ->
                if (childId in childIds) patch.copy(box = patch.box?.let { ungroupBox(group.base.box, it) }) else patch
            }) }, layers = siblings(page.layers, setOf(id)) { layers ->
            layers.flatMap { layer ->
                if (layer.base.id != id) listOf(layer) else {
                    require(layer is GroupLayer && !layer.clip && !layer.base.locked && layer.base.opacity == 1.0 &&
                        layer.base.shadow == null && !layer.base.hidden) { "Ungroup would change inherited appearance" } // i18n-ignore: internal validation diagnostic
                    layer.children.map { child -> child.withBase(child.base.copy(box = ungroupBox(layer.base.box, child.base.box))) }
                }
            }
        }))
    }

    fun duplicate(ids: Set<String>): EditCommand = edit { page ->
        val remap = mutableMapOf<String, String>()
        fun clone(layer: Layer): Layer {
            val id = nextId().also { remap[layer.base.id] = it }
            val copy = layer.withBase(layer.base.copy(id = id, role = null))
            return if (copy is GroupLayer) copy.copy(children = copy.children.map(::clone)) else copy
        }
        fun duplicateIn(layers: List<Layer>): List<Layer> = layers.flatMap { layer ->
            if (layer.base.id in ids && layer !is BackgroundLayer && !layer.base.locked) listOf(layer, clone(layer))
            else listOf(if (layer is GroupLayer && !layer.base.locked) layer.copy(children = duplicateIn(layer.children)) else layer)
        }
        val layers = duplicateIn(page.layers)
        page.copy(layers = layers, overrides = page.overrides.mapValues { (_, value) ->
            value.copy(patches = value.patches + value.patches.mapNotNull { (id, patch) -> remap[id]?.let { it to patch } })
        })
    }

    fun paste(layers: List<Layer>): EditCommand = edit { page ->
        fun clone(layer: Layer): Layer {
            require(layer !is BackgroundLayer) { "The background cannot be pasted" } // i18n-ignore: internal contract diagnostic
            val copy = layer.withBase(layer.base.copy(id = nextId(), role = null, locked = false))
            return if (copy is GroupLayer) copy.copy(children = copy.children.map(::clone)) else copy
        }
        page.copy(layers = page.layers + layers.map(::clone))
    }

    fun frame(id: String, frame: FrameKind): EditCommand = edit { page ->
        page.copy(layers = mapLayers(page.layers) {
            if (it.base.id == id && it is DeviceLayer && pathTo(page.layers, id).none { ancestor -> ancestor.base.locked }) it.copy(frame = frame) else it
        })
    }

    fun scene(input: SceneInput, arranger: SceneArranger): EditCommand = edit { arranger.apply(it, input) }

    fun localize(locale: String, ids: Set<String>, enabled: Boolean): EditCommand = edit(upgrade = true) { page ->
        val override = page.overrides[locale] ?: LocaleOverride()
        val patches = if (enabled) override.patches + ids.associateWith { override.patches[it] ?: LayerPatch() }
        else override.patches - ids
        page.copy(overrides = page.overrides + (locale to override.copy(patches = patches)))
    }

    fun patch(locale: String, id: String, patch: LayerPatch): EditCommand = edit(upgrade = true) { page ->
        val override = page.overrides[locale] ?: LocaleOverride()
        page.copy(overrides = page.overrides + (locale to override.copy(patches = override.patches + (id to patch))))
    }

    fun mirror(locale: String, enabled: Boolean?): EditCommand = edit(upgrade = true) { page ->
        val override = page.overrides[locale] ?: LocaleOverride()
        page.copy(overrides = page.overrides + (locale to override.copy(mirror = enabled)))
    }

    fun drop(id: String, targetId: String, inside: Boolean, after: Boolean): EditCommand = edit { page ->
        val sourcePath = pathTo(page.layers, id)
        val targetPath = pathTo(page.layers, targetId)
        val source = requireNotNull(sourcePath.lastOrNull())
        val destination = requireNotNull(targetPath.lastOrNull())
        require(source !is BackgroundLayer && destination !is BackgroundLayer && id != targetId &&
            targetPath.none { it.base.id == id } && sourcePath.none { it.base.locked } && targetPath.none { it.base.locked }) {
            "Cannot move a pinned, locked or cyclic layer" // i18n-ignore: internal validation diagnostic
        }
        val oldParent = sourcePath.dropLast(1).lastOrNull()?.let { dev.lingvo.shared.screenshots.geom.worldOf(page, it.base.id) }
            ?: dev.lingvo.shared.screenshots.render.Affine.IDENTITY
        val newParentLayer = if (inside) destination.also { require(it is GroupLayer) } else targetPath.dropLast(1).lastOrNull()
        val newParent = newParentLayer?.let { dev.lingvo.shared.screenshots.geom.worldOf(page, it.base.id) }
            ?: dev.lingvo.shared.screenshots.render.Affine.IDENTITY
        val moved = source.withBase(source.base.copy(box = reparentBox(source.base.box, oldParent, newParent)))
        fun remove(layers: List<Layer>): List<Layer> = layers.filter { it.base.id != id }.map {
            if (it is GroupLayer) it.copy(children = remove(it.children)) else it
        }
        fun trim(layers: List<Layer>): List<Layer> = layers.mapNotNull {
            if (it is GroupLayer) it.copy(children = trim(it.children)).takeIf { group -> group.children.isNotEmpty() } else it
        }
        fun insert(layers: List<Layer>): List<Layer> = layers.flatMap {
            when {
                it.base.id == targetId && inside && it is GroupLayer -> listOf(it.copy(children = it.children + moved))
                it.base.id == targetId -> if (after) listOf(it, moved) else listOf(moved, it)
                it is GroupLayer -> listOf(it.copy(children = insert(it.children)))
                else -> listOf(it)
            }
        }
        cleanPatches(page.copy(layers = trim(insert(remove(page.layers))), overrides = page.overrides.mapValues { (_, override) ->
            override.copy(patches = override.patches.mapValues { (layerId, patch) ->
                if (layerId == id) patch.copy(box = patch.box?.let { reparentBox(it, oldParent, newParent) }) else patch
            })
        }))
    }

    private fun boxes(ids: Set<String>, transform: (Box) -> Box): EditCommand = edit { page ->
        fun walk(layers: List<Layer>, blocked: Boolean = false): List<Layer> = layers.map { layer ->
            val locked = blocked || layer.base.locked
            if (!locked && layer !is BackgroundLayer && layer.base.id in ids) {
                val box = transform(layer.base.box)
                if (layer is DeviceLayer) require(kotlin.math.abs(box.w / box.h - layer.base.box.w / layer.base.box.h) < 1e-8) {
                    "Device frames must keep their aspect ratio" // i18n-ignore: internal validation diagnostic
                }
                withResizedBox(layer, box)
            } else if (layer is GroupLayer) layer.copy(children = walk(layer.children, locked)) else layer
        }
        page.copy(layers = walk(page.layers))
    }

    private fun edit(upgrade: Boolean = false, change: (Page) -> Page): EditCommand = EditCommand { original ->
        val design = if (upgrade) upgradeLocaleOverrides(original) else original
        require(design.boards.any { it.id == target.boardId && it.pages.any { page -> page.id == target.pageId } }) {
            "The edited page no longer exists" // i18n-ignore: internal validation diagnostic
        }
        val result = design.copy(boards = design.boards.map { board ->
            if (board.id != target.boardId) board else board.copy(pages = board.pages.map {
                if (it.id == target.pageId) change(it) else it
            })
        })
        val checked = DesignCodec.decode(DesignCodec.encode(result), StoreDeviceClassRule)
        require(checked is DecodeResult.Ok) { "Edit refused: $checked" } // i18n-ignore: internal contract diagnostic
        result
    }
}

internal fun mapLayers(layers: List<Layer>, transform: (Layer) -> Layer): List<Layer> = layers.map {
    transform(if (it is GroupLayer) it.copy(children = mapLayers(it.children, transform)) else it)
}

internal fun allLayers(layers: List<Layer>): List<Layer> = layers.flatMap {
    listOf(it) + if (it is GroupLayer) allLayers(it.children) else emptyList()
}

private fun siblings(layers: List<Layer>, ids: Set<String>, change: (List<Layer>) -> List<Layer>): List<Layer> {
    if (layers.any { it.base.id in ids }) {
        require(layers.count { it.base.id in ids } == ids.size) { "Selected layers must share a parent" } // i18n-ignore: internal contract diagnostic
        return change(layers)
    }
    return layers.map { if (it is GroupLayer && !it.base.locked) it.copy(children = siblings(it.children, ids, change)) else it }
}

private fun cleanPatches(page: Page): Page {
    val ids = allLayers(page.layers).map { it.base.id }.toSet()
    return page.copy(overrides = page.overrides.mapValues { (_, value) -> value.copy(patches = value.patches.filterKeys { it in ids }) })
}

/** Exhaustive copying avoids reaching the engine module's internal mutation helper. */
internal fun Layer.withBase(base: LayerBase): Layer = when (this) {
    is BackgroundLayer -> copy(base = base)
    is DeviceLayer -> copy(base = base)
    is ImageLayer -> copy(base = base)
    is TextLayer -> copy(base = base)
    is ShapeLayer -> copy(base = base)
    is GroupLayer -> copy(base = base)
}
